import { HttpErrorResponse } from '@angular/common/http';
import { ErrorHandler, Injectable, inject } from '@angular/core';
import { ErrorReferenceService } from './error-reference.service';
import { TRACE_CONFIG } from './trace.config';
import { TraceService } from './trace.service';
import { TelemetryService } from './telemetry.service';

@Injectable({ providedIn: 'root' })
export class GlobalErrorHandler implements ErrorHandler {
  private readonly trace = inject(TraceService);
  private readonly telemetry = inject(TelemetryService);
  private readonly errorReference = inject(ErrorReferenceService);

  // Template and getter errors are re-thrown on every change-detection pass as a fresh object,
  // so identity-based dedup alone would open a toast (and emit a RUM event) per cycle.
  private lastKey: string | null = null;
  private lastShownAt = 0;

  /** Reference of the most recent error shown to the user, or null. Safe for error pages. */
  get latestReference(): string | null {
    return this.errorReference.latestReference;
  }

  handleError(error: unknown): void {
    // eslint-disable-next-line no-console
    console.error(error);
    // Gate RUM recording as well as the toast: a template error re-thrown on every
    // change-detection pass must not send a RUM error per pass.
    if (this.isRepeat(error)) {
      return;
    }
    this.telemetry.recordError(error);
    if (!this.trace.isEnabled()) {
      return;
    }

    // Auth failures already have dedicated UI in the token interceptor.
    if (
      error instanceof HttpErrorResponse &&
      (error.status === 401 || error.status === 403)
    ) {
      return;
    }

    // A component already showed this error (with its reference): no second toast.
    if (this.errorReference.isHandled(error)) {
      return;
    }

    // referenceFor records the error_shown RUM event.
    const reference = this.errorReference.referenceFor(error ?? new Error('Unknown error'));
    if (!reference) {
      return;
    }
    // eslint-disable-next-line no-console
    console.error(`Error reference: ${reference}`);
    this.errorReference.openReferenceSnackbar('Something went wrong.', reference);
  }

  /** True when the same error (by message and top stack frame) was handled within the toast's lifetime. */
  private isRepeat(error: unknown): boolean {
    const key = this.keyOf(error);
    const now = Date.now();
    const repeat = key === this.lastKey && now - this.lastShownAt < TRACE_CONFIG.toastDurationMs;
    if (!repeat) {
      this.lastKey = key;
      this.lastShownAt = now;
    }
    return repeat;
  }

  private keyOf(error: unknown): string {
    const unwrapped =
      typeof error === 'object' && error !== null && 'rejection' in error
        ? (error as { rejection: unknown }).rejection
        : error;
    if (unwrapped instanceof Error) {
      const frame = unwrapped.stack?.split('\n').find((line) => line.trim().startsWith('at ')) ?? '';
      return `${unwrapped.name}:${unwrapped.message}:${frame.trim()}`;
    }
    if (unwrapped instanceof HttpErrorResponse) {
      return `http:${unwrapped.status}:${unwrapped.url ?? ''}`;
    }
    return String(unwrapped);
  }
}
